// Mirrors backend StateEnum (recommendation-service/app/enums/state.py).
export const STATE_ID = {
  ALABAMA: 1,
  ALASKA: 2,
  ARIZONA: 3,
  ARKANSAS: 4,
  CALIFORNIA: 5,
  COLORADO: 6,
  CONNECTICUT: 7,
  DELAWARE: 8,
  FLORIDA: 9,
  GEORGIA: 10,
  HAWAII: 11,
  IDAHO: 12,
  ILLINOIS: 13,
  INDIANA: 14,
  IOWA: 15,
  KANSAS: 16,
  KENTUCKY: 17,
  LOUISIANA: 18,
  MAINE: 19,
  MARYLAND: 20,
  MASSACHUSETTS: 21,
  MICHIGAN: 22,
  MINNESOTA: 23,
  MISSISSIPPI: 24,
  MISSOURI: 25,
  MONTANA: 26,
  NEBRASKA: 27,
  NEVADA: 28,
  NEW_HAMPSHIRE: 29,
  NEW_JERSEY: 30,
  NEW_MEXICO: 31,
  NEW_YORK: 32,
  NORTH_CAROLINA: 33,
  NORTH_DAKOTA: 34,
  OHIO: 35,
  OKLAHOMA: 36,
  OREGON: 37,
  PENNSYLVANIA: 38,
  RHODE_ISLAND: 39,
  SOUTH_CAROLINA: 40,
  SOUTH_DAKOTA: 41,
  TENNESSEE: 42,
  TEXAS: 43,
  UTAH: 44,
  VERMONT: 45,
  VIRGINIA: 46,
  WASHINGTON: 47,
  WEST_VIRGINIA: 48,
  WISCONSIN: 49,
  WYOMING: 50,
  GUAM: 51,
  PUERTO_RICO: 52,
  VIRGIN_ISLANDS: 53,
  DISTRICT_OF_COLUMBIA: 54,
} as const satisfies Record<string, number>;

export type StateKey = keyof typeof STATE_ID;

export type StateId = (typeof STATE_ID)[StateKey];

export const STATE_ABBREVIATION = {
  ALABAMA: "AL",
  ALASKA: "AK",
  ARIZONA: "AZ",
  ARKANSAS: "AR",
  CALIFORNIA: "CA",
  COLORADO: "CO",
  CONNECTICUT: "CT",
  DELAWARE: "DE",
  FLORIDA: "FL",
  GEORGIA: "GA",
  HAWAII: "HI",
  IDAHO: "ID",
  ILLINOIS: "IL",
  INDIANA: "IN",
  IOWA: "IA",
  KANSAS: "KS",
  KENTUCKY: "KY",
  LOUISIANA: "LA",
  MAINE: "ME",
  MARYLAND: "MD",
  MASSACHUSETTS: "MA",
  MICHIGAN: "MI",
  MINNESOTA: "MN",
  MISSISSIPPI: "MS",
  MISSOURI: "MO",
  MONTANA: "MT",
  NEBRASKA: "NE",
  NEVADA: "NV",
  NEW_HAMPSHIRE: "NH",
  NEW_JERSEY: "NJ",
  NEW_MEXICO: "NM",
  NEW_YORK: "NY",
  NORTH_CAROLINA: "NC",
  NORTH_DAKOTA: "ND",
  OHIO: "OH",
  OKLAHOMA: "OK",
  OREGON: "OR",
  PENNSYLVANIA: "PA",
  RHODE_ISLAND: "RI",
  SOUTH_CAROLINA: "SC",
  SOUTH_DAKOTA: "SD",
  TENNESSEE: "TN",
  TEXAS: "TX",
  UTAH: "UT",
  VERMONT: "VT",
  VIRGINIA: "VA",
  WASHINGTON: "WA",
  WEST_VIRGINIA: "WV",
  WISCONSIN: "WI",
  WYOMING: "WY",
  GUAM: "GU",
  PUERTO_RICO: "PR",
  VIRGIN_ISLANDS: "VI",
  DISTRICT_OF_COLUMBIA: "DC",
} as const satisfies Record<StateKey, string>;

export type StateAbbreviation = (typeof STATE_ABBREVIATION)[StateKey];

export const stateAbbreviations = Object.values(STATE_ABBREVIATION) as [
  StateAbbreviation,
  ...StateAbbreviation[],
];

const STATE_ABBREVIATION_SET = new Set<string>(
  Object.values(STATE_ABBREVIATION),
);

export const isStateAbbreviation = (
  value: string,
): value is StateAbbreviation => STATE_ABBREVIATION_SET.has(value);

export const STATE_NAME = {
  ALABAMA: "Alabama",
  ALASKA: "Alaska",
  ARIZONA: "Arizona",
  ARKANSAS: "Arkansas",
  CALIFORNIA: "California",
  COLORADO: "Colorado",
  CONNECTICUT: "Connecticut",
  DELAWARE: "Delaware",
  FLORIDA: "Florida",
  GEORGIA: "Georgia",
  HAWAII: "Hawaii",
  IDAHO: "Idaho",
  ILLINOIS: "Illinois",
  INDIANA: "Indiana",
  IOWA: "Iowa",
  KANSAS: "Kansas",
  KENTUCKY: "Kentucky",
  LOUISIANA: "Louisiana",
  MAINE: "Maine",
  MARYLAND: "Maryland",
  MASSACHUSETTS: "Massachusetts",
  MICHIGAN: "Michigan",
  MINNESOTA: "Minnesota",
  MISSISSIPPI: "Mississippi",
  MISSOURI: "Missouri",
  MONTANA: "Montana",
  NEBRASKA: "Nebraska",
  NEVADA: "Nevada",
  NEW_HAMPSHIRE: "New Hampshire",
  NEW_JERSEY: "New Jersey",
  NEW_MEXICO: "New Mexico",
  NEW_YORK: "New York",
  NORTH_CAROLINA: "North Carolina",
  NORTH_DAKOTA: "North Dakota",
  OHIO: "Ohio",
  OKLAHOMA: "Oklahoma",
  OREGON: "Oregon",
  PENNSYLVANIA: "Pennsylvania",
  RHODE_ISLAND: "Rhode Island",
  SOUTH_CAROLINA: "South Carolina",
  SOUTH_DAKOTA: "South Dakota",
  TENNESSEE: "Tennessee",
  TEXAS: "Texas",
  UTAH: "Utah",
  VERMONT: "Vermont",
  VIRGINIA: "Virginia",
  WASHINGTON: "Washington",
  WEST_VIRGINIA: "West Virginia",
  WISCONSIN: "Wisconsin",
  WYOMING: "Wyoming",
  GUAM: "Guam",
  PUERTO_RICO: "Puerto Rico",
  VIRGIN_ISLANDS: "Virgin Islands",
  DISTRICT_OF_COLUMBIA: "District of Columbia",
} as const satisfies Record<StateKey, string>;

export type StateName = (typeof STATE_NAME)[StateKey];

const ABBREVIATION_BY_STATE_ID: Record<number, StateAbbreviation> =
  Object.fromEntries(
    Object.entries(STATE_ID).map(([name, id]) => [
      id,
      STATE_ABBREVIATION[name as StateKey],
    ]),
  );

const STATE_ID_BY_ABBREVIATION: Record<string, StateId> = Object.fromEntries(
  Object.entries(STATE_ABBREVIATION).map(([name, abbreviation]) => [
    abbreviation,
    STATE_ID[name as StateKey],
  ]),
);

export function stateAbbreviationFromId(id: number): StateAbbreviation | "" {
  return ABBREVIATION_BY_STATE_ID[id] ?? "";
}

export function stateIdFromAbbreviation(abbreviation: string): StateId | null {
  return STATE_ID_BY_ABBREVIATION[abbreviation] ?? null;
}
